'use client';

import { useState, type ReactNode } from 'react';
import {
  ChatCircleDots,
  Dog,
  Stethoscope,
  Crown,
} from '@phosphor-icons/react';

interface BillingToggleProps {
  plusMonthly: number;
  plusAnnualPerMonth: number;
  plusAnnualTotal: number;
  proMonthly: number;
  proAnnualPerMonth: number;
  proAnnualTotal: number;
  freeFeatures: ReactNode;
  plusFeatures: ReactNode;
  proFeatures: ReactNode;
  freeCta: ReactNode;
  plusCta: ReactNode;
  proCta: ReactNode;
}

export default function BillingToggle({
  plusMonthly,
  plusAnnualPerMonth,
  plusAnnualTotal,
  proMonthly,
  proAnnualPerMonth,
  proAnnualTotal,
  freeFeatures,
  plusFeatures,
  proFeatures,
  freeCta,
  plusCta,
  proCta,
}: BillingToggleProps) {
  const [isAnnual, setIsAnnual] = useState(true); // Default to annual (most popular)

  return (
    <>
      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3 mb-8">
        <button
          onClick={() => setIsAnnual(false)}
          className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors ${
            !isAnnual
              ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
              : 'text-gray-500 hover:text-gray-600'
          }`}
        >
          Monthly
        </button>
        <button
          onClick={() => setIsAnnual(true)}
          className={`text-sm font-medium px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 ${
            isAnnual
              ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
              : 'text-gray-500 hover:text-gray-600'
          }`}
        >
          Annual
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${
            isAnnual ? 'bg-teal-400 text-white' : 'bg-teal-100 text-teal-700 dark:text-teal-300'
          }`}>
            -30%
          </span>
        </button>
      </div>

      {/* Tier cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* ---- FREE TIER ---- */}
        <div className="rounded-2xl border border-gray-200 bg-surface p-6 flex flex-col">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Dog weight="duotone" className="w-5 h-5 text-gray-500" />
              <h2 className="font-heading font-semibold text-dark">Free</h2>
            </div>
            <p className="text-xs text-gray-500">For curious dog owners</p>
          </div>

          <div className="flex items-baseline gap-1 mb-6">
            <span className="text-3xl font-heading font-bold text-dark">&euro;0</span>
            <span className="text-gray-400 text-sm">/month</span>
          </div>

          {freeFeatures}

          <div className="mt-auto pt-6">
            {freeCta}
          </div>
        </div>

        {/* ---- PLUS TIER ---- */}
        <div className="rounded-2xl border-2 border-teal-500 bg-surface p-6 flex flex-col relative">
          {/* Popular badge — always on Plus */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-teal-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
              Most popular
            </span>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <ChatCircleDots weight="duotone" className="w-5 h-5 text-teal-500" />
              <h2 className="font-heading font-semibold text-dark">Plus</h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-600 border border-teal-200">
                Launch price
              </span>
            </div>
            <p className="text-xs text-gray-500">For dedicated dog parents</p>
          </div>

          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-3xl font-heading font-bold text-dark">
              &euro;{isAnnual ? plusAnnualPerMonth.toFixed(2) : plusMonthly.toFixed(2)}
            </span>
            <span className="text-gray-400 text-sm">/month</span>
          </div>
          {isAnnual ? (
            <p className="text-xs text-gray-400 mb-6">
              &euro;{plusAnnualTotal.toFixed(2)} billed annually. Cancel anytime.
            </p>
          ) : (
            <p className="text-xs text-gray-400 mb-6">
              At less than the price of 1 vet visit per year. Cancel anytime.
            </p>
          )}

          {plusFeatures}

          <div className="mt-auto pt-6">
            {plusCta}
          </div>
        </div>

        {/* ---- PRO TIER (Veterinarians) ---- */}
        <div className="rounded-2xl bg-gradient-to-br from-surface to-amber-light/40 border border-gray-200 p-6 flex flex-col relative">
          {/* Vet badge — always on Pro */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
              <Stethoscope weight="bold" className="w-3 h-3" />
              For vets
            </span>
          </div>

          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Crown weight="duotone" className="w-5 h-5 text-amber-500" />
              <h2 className="font-heading font-semibold text-dark">Pro</h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-light text-amber border border-amber/30">
                Launch price
              </span>
            </div>
            <p className="text-xs text-gray-500">For veterinary professionals</p>
          </div>

          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-3xl font-heading font-bold text-dark">
              &euro;{isAnnual ? proAnnualPerMonth.toFixed(2) : proMonthly}
            </span>
            <span className="text-gray-400 text-sm">/month</span>
          </div>
          {isAnnual ? (
            <p className="text-xs text-gray-400 mb-6">
              &euro;{proAnnualTotal.toFixed(2)} billed annually
            </p>
          ) : (
            <p className="text-xs text-gray-400 mb-6">
              Unlimited access for your practice
            </p>
          )}

          {proFeatures}

          <div className="mt-auto pt-6">
            {proCta}
          </div>
        </div>
      </div>
    </>
  );
}
